<template>
  <div class="schedule-wrapper" :class="targetAuthorData ? 'author-view' : ''">
    <v-img
      src="@/assets/images/BF_Logo_Full_CROPPED.svg"
      contain
      max-height="140"
      class="nobf-logo mb-5 mt-2"
      eager
    />
    <h2 v-if="targetAuthorData && targetAuthorData.fullName">
      Speaker Name: {{ targetAuthorData.fullName }}
    </h2>

    <h2 v-if="mergedView && mergedAuthorNames">
      Speaker(s): {{ mergedAuthorNames }}
    </h2>
    <div class="rsvp-messages">
      <p v-if="requiresRsvp" class="error--text">
        <v-icon class="error--text">mdi-alert</v-icon>
        <strong>You have events which require an RSVP.</strong>
      </p>
      <p v-if="!requiresRsvp && alreadyRsvped">
        <strong>We appreciate your RSVP. For any changes to your planned schedule, please contact us.</strong>
      </p>
    </div>
    <table class="schedule-table">
      <tr>
        <td>Event Date:</td>
        <td v-if="formattedDateRange">
          {{ formattedDateRange }}
        </td>
      </tr>
      <tr>
        <td>Location:</td>
        <td>New Orleans, LA</td>
      </tr>
      <tr>
        <td>Organization:</td>
        <td>New Orleans Book Festival</td>
      </tr>
      <tr v-if="hasBooksSold">
        <td>Books Sold:</td>
        <td>
          <div
            v-for="(book, index) in targetAuthorData.booksSold"
            :key="`book-${index}`"
          >
            <em>{{ book }}</em>
          </div>
        </td>
      </tr>
      <tr>
        <td>Attire:</td>
        <td>Business Casual</td>
      </tr>
      <tr>
        <td>Arrival Time:</td>
        <td>Please arrive onsite 30 minutes before your scheduled session.</td>
      </tr>
      <tr v-if="targetAuthorData && targetAuthorData.onsiteContacts && targetAuthorData.onsiteContacts.length && targetAuthorData.onsiteContacts[0].name">
        <td>Onsite Contact(s):</td>
        <td>
          <div
            v-for="(contact, index) in targetAuthorData.onsiteContacts"
            :key="`contact-${index}`"
          >
            {{ contact.name || '' }}<!--
            --><template v-if="contact.phone">, <a :href="`tel:${contact.phone}`">{{ contact.phone }}</a></template><!--
            --><template v-if="contact.email">, <a v-if="contact.email" :href="`mailto:${contact.email}`">{{ contact.email }}</a></template>
          </div>
        </td>
      </tr>
      <tr v-if="ambassador">
        <td>Ambassador:</td>
        <td>
          {{ ambassador.firstName }} {{ ambassador.lastName }}<br>
          <a :href="`mailto:${ambassador.email}`">{{ ambassador.email }}</a><br>
          <a :href="`tel:${ambassador.phone}`">{{ ambassador.phone }}</a>
        </td>
      </tr>
    </table>
    <div v-if="targetAuthorData" class="top-notes">
      <!-- <strong><p>If you cannot reach your author ambassador, please contact the author hotline at
        <a href="tel:+15043142222" style="text-decoration:none;">(504) 314-2222</a>
      </p></strong> -->
      <strong>
        <p>
          Please note, any disruption during sessions caused by attendees will be handled by security and event staff. Please do not address any issues from the stage.
        </p>
      </strong>
    </div>

    <!-- Only show this button if there's a target author, and they have events specific to them -->
    <v-btn
      v-if="targetAuthorData && myEvents && myEvents.length"
      color="accent"
      class="my-events-toggle mb-3"
      @click="splitView = !splitView"
    >
      {{ splitView ? 'Show my full schedule' : 'Show my confirmed session(s)'}}
    </v-btn>
    <!-- Events Specific to author -->
    <template v-if="useSplitView && myEvents && myEvents.length">
      <h4 class="primary white--text">Your Confirmed Session(s)</h4>
      <ScheduleSingleTable
        :eventHeaders="eventHeaders"
        :events="myEvents"
        :mergedView="mergedView"
        :targetAuthorData="targetAuthorData"
        :rsvpTotals="rsvpTotals"
        :rsvpAuthorsByEventId="rsvpAuthorsByEventId"
      />
    </template>

    <!-- All events or all other events -->
    <h4 v-if="useSplitView && myEvents && myEvents.length" class="primary white--text other-events">Additional Festival Events</h4>
    <ScheduleSingleTable
      v-if="events && events.length"
      :eventHeaders="eventHeaders"
      :events="useSplitView ? otherEvents : events"
      :mergedView="mergedView"
      :targetAuthorData="targetAuthorData"
      :rsvpTotals="rsvpTotals"
      :rsvpAuthorsByEventId="rsvpAuthorsByEventId"
    />

    <v-progress-circular v-else-if="!eventsInitialized" indeterminate size="80" color="primary"></v-progress-circular>
    <p v-else>It looks like there aren't any related events for this author.</p>

    <!-- Flight(s) -->
    <section v-if="targetAuthorData && targetAuthorData.airlineInfo">
      <hr>
      <h2>Airline Information</h2>
      <pre v-html="targetAuthorData.airlineInfo"></pre>
    </section>

    <!-- Hotel -->
    <section v-if="targetAuthorHotelData" class="hotel">
      <hr>
      <h2>Hotel Information</h2>
      <HotelCard :hotel="targetAuthorHotelData" />
    </section>

    <!-- Additional Notes -->
    <section v-if="targetAuthorData && targetAuthorData.notes">
      <hr>
      <h2>Additional Notes</h2>
      <pre>{{ targetAuthorData.notes }}</pre>
    </section>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { getDoc, doc } from 'firebase/firestore'
import HotelCard from '@/components/HotelCard'
import ScheduleSingleTable from '@/components/ScheduleSingleTable'

export default {
  name: 'ScheduleSingle',
  components: {
    HotelCard,
    ScheduleSingleTable
  },
  props: {
    targetAuthorData: {
      type: Object,
      default: () => {}
    },
    ambassador: {
      type: Object,
      default: () => {}
    },
    // In the event there are multiple authors in the same schedule (for an ambassador)
    authors: {
      type: Array,
      default: () => []
    },
    events: {
      type: Array,
      default: () => []
    },
    eventHeaders: {
      type: Array,
      default: () => []
    },
    formattedDateRange: {
      type: String,
      default: ''
    },
    rsvpTotals: {
      type: Object,
      default: () => {}
    },
    rsvpAuthorsByEventId: {
      type: Object,
      default: () => {}
    },
    mergedView: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    splitView: true,
    targetAuthorHotelData: null
  }),
  computed: {
    ...mapState([
      'firestore'
    ]),
    ...mapGetters([
      'requiresRsvp',
      'alreadyRsvped',
      'eventsInitialized'
    ]),

    useSplitView () {
      return this.splitView && !!this.targetAuthorData
    },

    hasBooksSold () {
      return (
        this.targetAuthorData &&
        this.targetAuthorData.booksSold &&
        this.targetAuthorData.booksSold.length > 0 &&
        this.targetAuthorData.booksSold[0] !== ''
      )
    },
    mergedAuthorNames () {
      let merged = ''
      this.authors.forEach((author, i) => {
        merged += author.fullName
        if (i < this.authors.length - 1) merged += ', '
      })
      return merged
    },
    // Filter out only events which are specific to this author
    myEvents () {
      if (!this.events) return []
      const myE = []
      this.events.forEach((e) => {
        if (e.tags) {
          e.tags.forEach((t) => {
            if (this.targetAuthorData && t === this.targetAuthorData.id) {
              myE.push(e)
            }
          })
        }
      })
      return myE
    },
    otherEvents () {
      if (!this.events) return []
      const otherE = []
      this.events.forEach((e) => {
        if (e.tags) {
          if (!e.tags.includes(this.targetAuthorData.id)) {
            otherE.push(e)
          }
        } else {
          otherE.push(e)
        }
      })
      return otherE
    }
  },

  async created () {
    if (this.targetAuthorData?.hotel) {
      try {
        const hotel = await getDoc(doc(this.firestore, 'hotels', this.targetAuthorData.hotel))
        this.targetAuthorHotelData = hotel.data()
      } catch (err) {
        console.log(err)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.schedule-wrapper {
  .v-progress-circular {
    display: block;
    margin: 0 auto;
  }

  &.author-view {
    ::v-deep .v-data-footer {
      display: none !important;
    }
  }
}
.schedule-table {
  td {
    padding: 5px 0;

    a {
      text-decoration: none;
    }
  }
  td:nth-child(1) {
    padding-right: 20px;
    font-weight: 700;
    vertical-align: top;
  }

  @media print {
    td {
      padding-top: 0;
      padding-bottom: 0;
      font-size: 12px;
    }
  }
}

h4 {
  padding: 1em;
  border-radius: 4px;
  background-color: #17396380;
}

.other-events {
  margin-top: 1em;
  border-top: solid 1px rgba(0, 0, 0, 0.12);
}

section {
  padding: 20px 0 0;
  h2 {
    margin: 10px 0;
  }
}

@media print {
  .rsvp-messages {
    display: none;
  }
  .nobf-logo {
    height: 100px;
  }
  .my-events-toggle {
    display: none;
  }
}
</style>
